import { ChatMessagesSkeleton } from '@/chat/components/ChatMessagesSkeleton'
import {
  FormValues,
  PlaybookDeleteModal,
} from '@/playbooks/components/PlaybookDeleteModal'
import { PlaybookHeader } from '@/playbooks/components/PlaybookHeader'
import { ApiError } from '@/services/api'
import { SidebarLayout } from '@/shared/components/SidebarLayout'
import { useDeletePlaybook } from '@/shared/hooks/data-hooks/playbooks/useDeletePlaybook'
import { useDuplicatePlaybook } from '@/shared/hooks/data-hooks/playbooks/useDuplicatePlaybook'
import { usePlaybook } from '@/shared/hooks/data-hooks/playbooks/usePlaybook'
import { usePlaybookMessages } from '@/shared/hooks/data-hooks/playbooks/usePlaybookMessages'
import { useUpdatePlaybook } from '@/shared/hooks/data-hooks/playbooks/useUpdatePlaybook'
import { useIsMobile } from '@/shared/hooks/useMobile'
import { useSplitView } from '@/shared/hooks/useSplitView'
import { useToast } from '@/shared/hooks/useToast'
import { cn } from '@/shared/lib/utils'
import {
  Playbook,
  PlaybookUserAction,
  PlaybookUserActionPayload,
} from '@/types/playbook'
import { keepPreviousData } from '@tanstack/react-query'
import React, { lazy, Suspense, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PlaybookQueryProvider } from '../components/PlaybookQueryProvider'
import { PlaybookViewLoadingState } from '../components/PlaybookViewLoadingState'
import { PlaybookViewProvider } from '../contexts'
import { usePlaybookQueryContext } from '../hooks/usePlaybookQueryContext'
const PlaybookView = lazy(() => import('../components/PlaybookView'))
const PlaybookChatMessages = lazy(
  () => import('../components/PlaybookChatMessages')
)

const DocumentTitleUpdater: React.FC = () => {
  const { title } = usePlaybookQueryContext()

  useEffect(() => {
    if (title) {
      document.title = `${title} | Mydra`
    } else {
      document.title = 'Playbook | Mydra'
    }
  }, [title])

  return null
}

const PlaybookPage: React.FC = () => {
  const { id } = useParams()
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = React.useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()
  const isMobile = useIsMobile()

  const { mutateAsync: deletePlaybook } = useDeletePlaybook()
  const { mutateAsync: duplicatePlaybook } = useDuplicatePlaybook()
  const { mutateAsync: updatePlaybook } = useUpdatePlaybook()

  const { data, isLoading: isPlaybookLoading } = usePlaybook({
    id: id!,
    queryOptions: {
      placeholderData: keepPreviousData,
      throwOnError: (error) => {
        return (
          error instanceof ApiError &&
          (error.statusCode >= 400 || error.statusCode < 500)
        )
      },
    },
  })

  const {
    error: errorLoadingMessages,
    data: { data: chatMessages } = {},
    isLoading: isPlaybookMessagesLoading,
  } = usePlaybookMessages({
    id,
    queryOptions: {
      throwOnError: false,
    },
  })

  const playbook = data?.data

  const isAccessDeniedLoadingMessages =
    errorLoadingMessages &&
    errorLoadingMessages instanceof ApiError &&
    errorLoadingMessages.statusCode === 403

  const { leftPanelRef: chatPanelRef, rightPanelRef: contentPanelRef } =
    useSplitView({
      storageKey: 'playbook-split-proportions',
      defaultSizes: [40, 60],
      minSizes: [0, 400],
      disabled: isMobile || isAccessDeniedLoadingMessages,
    })

  useEffect(() => {
    window.Beacon?.('config', {
      hideFABOnMobile: true,
    })

    return () => {
      window.Beacon?.('config', {
        hideFABOnMobile: false,
      })
    }
  }, [])

  const handleDeleteConfirmation = async ({ confirmed }: FormValues) => {
    if (!playbook?.playbook_id || !confirmed) {
      setIsConfirmDeleteOpen(false)
      return
    }

    try {
      await deletePlaybook(playbook.playbook_id)
      setIsConfirmDeleteOpen(false)
      toast({
        title: 'Playbook deleted',
        description: 'The playbook has been removed successfully.',
      })
      navigate('/dashboard')
    } catch {
      toast({
        title: 'Error deleting playbook',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleRename = async (_playbook: Playbook, newTitle: string) => {
    try {
      await updatePlaybook({
        id: playbook.playbook_id,
        playbookData: { title: newTitle },
      })
      toast({
        title: 'Playbook renamed',
        description: 'The playbook has been renamed successfully.',
      })
    } catch {
      toast({
        title: 'Error renaming playbook',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleDuplicate = async (_playbook?: Playbook) => {
    const sourceId = _playbook?.playbook_id || id
    if (!sourceId) return
    try {
      const { data: duplicatedPlaybook } = await duplicatePlaybook({
        fromPlaybookId: sourceId,
      })

      toast({
        title: 'Playbook duplicated',
        description: 'Redirecting to the duplicated playbook…',
      })
      navigate(`/playbooks/${duplicatedPlaybook._id}`)
    } catch {
      toast({
        title: 'Error duplicating playbook',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handlePlaybookAction = async (
    action: PlaybookUserAction,
    payload?: PlaybookUserActionPayload<typeof action>
  ) => {
    switch (action) {
      case 'delete':
        setIsConfirmDeleteOpen(true)
        break
      case 'duplicate':
        await handleDuplicate(playbook)
        break
      case 'rename': {
        const data = payload as PlaybookUserActionPayload<typeof action>
        if (data?.playbook && data?.newTitle) {
          await handleRename(data.playbook, data.newTitle)
        }
        break
      }
      case 'visibility': {
        const data = payload as PlaybookUserActionPayload<typeof action>

        if (data?.playbook && data.visibility) {
          try {
            await updatePlaybook({
              id: data.playbook.playbook_id,
              playbookData: { isPublic: data.visibility === 'public' },
            })
            toast({
              title: 'Visibility updated',
              description: `Playbook is now ${data.visibility}.`,
            })
          } catch {
            toast({
              title: 'Error updating visibility',
              description: 'Please try again.',
              variant: 'destructive',
            })
          }
        }
        break
      }
      default:
        break
    }
  }

  return (
    <PlaybookViewProvider>
      {({ isShown }) => (
        <PlaybookQueryProvider
          playbookId={id}
          queryOptions={{
            initialData: data,
          }}
        >
          <DocumentTitleUpdater />
          <SidebarLayout
            className="PlaybookPage bg-grey-02"
            noSidebarToggle
            header={
              <PlaybookHeader
                onPlaybookAction={handlePlaybookAction}
                className="z-30 md:z-auto"
              />
            }
          >
            <div className="relative h-full gap-5 overflow-hidden md:h-full md:flex-row">
              <div className="h-full md:flex md:flex-row md:gap-0">
                <div
                  ref={chatPanelRef}
                  className="h-full overflow-hidden empty:hidden md:flex-shrink-0"
                >
                  {!isAccessDeniedLoadingMessages && (
                    <Suspense
                      fallback={
                        <div className="flex-1 px-4 py-4 md:px-6">
                          <ChatMessagesSkeleton />
                        </div>
                      }
                    >
                      <PlaybookChatMessages
                        isLoading={isPlaybookMessagesLoading}
                        chatMessages={chatMessages}
                        playbookId={id!}
                        playbook={playbook}
                        className="flex-1 px-4 py-4 md:px-6"
                      />
                    </Suspense>
                  )}
                </div>

                <div
                  ref={contentPanelRef}
                  className={cn(
                    isShown || isAccessDeniedLoadingMessages
                      ? 'translate-x-0'
                      : 'translate-x-full',
                    'absolute top-0 h-full w-full flex-1 overflow-y-auto py-4 transition-transform duration-300',
                    'md:relative md:flex-1 md:flex-shrink-0 md:translate-x-0 md:overflow-hidden md:overflow-y-auto md:px-5 md:transition-none'
                  )}
                >
                  <div className="relative min-h-full rounded-xl border-y bg-white p-4 md:rounded-lg md:border md:px-[10%]">
                    <Suspense fallback={<PlaybookViewLoadingState />}>
                      <PlaybookView
                        playbook={playbook}
                        onEdit={() => {
                          // TODO: Implement edit functionality
                        }}
                        isLoading={isPlaybookLoading}
                      />
                    </Suspense>
                  </div>
                </div>
              </div>
            </div>
            <PlaybookDeleteModal
              isOpen={isConfirmDeleteOpen}
              playbook={playbook}
              onSubmit={handleDeleteConfirmation}
            />
          </SidebarLayout>
        </PlaybookQueryProvider>
      )}
    </PlaybookViewProvider>
  )
}

export default PlaybookPage
